import React, { useLayoutEffect } from 'react';
import { IoArrowBackOutline, IoDocumentOutline } from 'react-icons/io5';
import { FaUserCircle, FaClock } from 'react-icons/fa';
import { LoadingOutlined } from '@ant-design/icons';
import Hls from 'hls.js';

import { humaniseFileSize } from 'helpers/document.helpers';
import { AphPageLoader, useFetchServiceV3 } from '@aph/common-react';
import {
  IUseSearchService,
  IGetLectureDetailsResponse
} from 'services';

import './LectureVideoView.scss';

/**
 * Create Markup
 *
 * @description Util function for generating the InnerHTML object used when dangerously setting innerHTML
 * @property {string} htmlString - HTML string
 * @returns {object} Dangerously setInnerHTML object
 */
const createMarkup = (htmlString: string): { __html: string; } => ({ __html: htmlString });

/**
 * ILectureVideoView
 *
 * @description Component properties for LectureVideoView
 * @property {string}                 videoId            Selected video ID
 * @property {() => void | undefined} onBackClickHandler On back click event
 */
interface ILectureVideoView {
  videoId: string;
  searchService: IUseSearchService;
  onBackClickHandler: () => void;
}

/**
 * LectureVideoView
 *
 * @description Displays the selected lecture video
 */
const LectureVideoView: React.FC<ILectureVideoView> = ({ searchService, videoId, onBackClickHandler }) => {
  const { lecture, errors } = useFetchServiceV3<string, IGetLectureDetailsResponse>(searchService.getLectureDetails, { initialRequest: videoId });

  const videoRef = React.createRef<HTMLVideoElement>();
  useLayoutEffect(() => {
    const jquery = (window as any).$;

    if (lecture?.additionalContent && jquery) {
      // This is a requirement to get the Foundation accordion working
      jquery(document).foundation('accordion', 'reflow');
    }
  }, [lecture]);

  useLayoutEffect(() => {
    if (lecture && videoRef.current) {
      const video = videoRef.current;

      if (Hls.isSupported() && lecture.url.includes('.m3u8')) {
        const hls = new Hls();
        hls.loadSource(lecture.url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play();
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = lecture.url;
        video.addEventListener('loadedmetadata', () => {
          video.play();
        });
      }
    }
  }, [lecture, videoRef]);

  if (errors) {
    onBackClickHandler();

    return null;
  }

  if (!lecture) {
    return <AphPageLoader text='Loading lecture...' />;
  }

  return (
    <div className='lecture-video-page library-publication'>
      <button
        type='button'
        className='lecture-video-page__back-button'
        onClick={onBackClickHandler}
      >
        <IoArrowBackOutline />
        Back to search results
      </button>

      <article className='lecture-video-page__latest-result-item'>
        <div className='box'>
          <div className='switch-container switch_player wide'>
            <video
              title='Video player'
              id='universalPlayerIframe'
              ref={videoRef}
              controls
              src={lecture.url}
              className='universalPlayerIframe'
            >
              <track kind='captions' />
            </video>
          </div>
        </div>
        <h3>{lecture.title}</h3>
        {
          lecture
            ?
            <>
              {
                lecture.description &&
                <div className='lecture-description' dangerouslySetInnerHTML={{ __html: lecture.description }} />
              }
              {
                (lecture.slidesDocument || lecture.supportingDocument) &&
                <div className='lecture-video-page__documents'>
                  {
                    lecture.slidesDocument &&
                    <div key={lecture.slidesDocument.id} className='lecture-video-page__document'>
                      <IoDocumentOutline />
                      <a
                        href={lecture.slidesDocument.uri}
                        target='_blank'
                        rel='noreferrer'
                      >
                        Lecture slides
                      </a>
                      <span>{humaniseFileSize(lecture.slidesDocument.size)}</span>
                    </div>
                  }
                  {
                    lecture.supportingDocument &&
                    <div key={lecture.supportingDocument.id} className='lecture-video-page__document'>
                      <IoDocumentOutline />
                      <a
                        href={lecture.supportingDocument.uri}
                        target='_blank'
                        rel='noreferrer'
                      >
                        Supporting document
                      </a>
                      <span>{humaniseFileSize(lecture.supportingDocument.size)}</span>
                    </div>
                  }
                </div>
              }

              <div className='lecture-video-page__meta-data'>
                {
                  lecture.author &&
                  <p className='lecture-video-page__icon-description'>
                    <FaUserCircle /> {lecture.author}
                  </p>
                }
                {
                  (lecture.date || lecture.durationString) &&
                  <p className='lecture-video-page__icon-description'>
                    <FaClock /> {lecture.date && `${lecture.date} - `}{lecture.durationString} duration
                  </p>
                }

                {
                  lecture?.additionalContent &&
                  // Sitecore adds new line characters which when base64 encoded then decoded show up in the HTML
                  <div className='lecture-video-page__footer footer'>
                    <p dangerouslySetInnerHTML={createMarkup(atob(lecture.additionalContent).replaceAll('\\n', ''))}></p>
                  </div>
                }
              </div>
            </>
            :
            <div className='lecture-video-page__loading-content'>
              <LoadingOutlined />
              <p>Loading content</p>
            </div>
        }
      </article>
    </div>
  );
};

export default LectureVideoView;